import { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Table as RsTable, TableProps, Row } from 'rsuite'
import { Cell, HeaderCell } from 'rsuite-table'
import { useModalReady } from '../modalReady'

// --- styles ---

export type TActionCell = { className?: string, onClick?: any, label?: string }
export type TActionCellClick = (action: string, id: string, data: any) => void

type TTable = TableProps<any, any> & {
    handleCellAction?: (action: string) => TActionCell | null,
}

// --- table context ---

export type TableContext = {
    handleCellAction?: (action: string) => TActionCell | null,
}
const Context = createContext<TableContext>({
    handleCellAction(action) { return null },
})

export const useTable = () => {
    return useContext(Context)
}

// --- table ---

const Table = ({ children, handleCellAction: handleCellAction, ...rest }: TTable) => {
    const value: TableContext = {
        handleCellAction: handleCellAction,
    }

    //
    // Inside a dialog, wait until it has finished opening.
    //
    // An rsuite Table measures itself the moment it mounts. While a dialog is
    // still animating, the box it measures keeps changing, and under WebKit that
    // measurement never settles — it loops until React aborts the render with
    // "Maximum update depth exceeded" and the dialog is dead. See
    // components/modalReady.ts for what was measured and what does NOT help.
    //
    // Only the table waits. Everything else in a dialog renders immediately, so
    // a dialog without a table opens exactly as it did before.
    //
    const ready = useModalReady()
    if (!ready) {
        // Holds the space the table is about to take, so the dialog does not
        // visibly grow when it arrives.
        const platzhalter: React.CSSProperties = rest.fillHeight
            ? { height: '100%' }
            : { height: typeof rest.height === 'number' ? rest.height : 200 }
        return <div className={rest.className} style={platzhalter} />
    }

    return (
        <Context.Provider value={value} >
            <RsTable {...rest}>
                {children}
            </RsTable>
        </Context.Provider>
    )
}

// --- table cell action ---

export type TCellAction = { action: string, id: string, label?: string, data?: any, onClick?: TActionCellClick }
const CellAction = ({ action, id, label, data, onClick, ...rest }: TCellAction) => {
    const { t } = useTranslation()
    const predefinedLabel = () => {
        switch (action.toLowerCase()) {
            case 'edit': return t('Edit')
            case 'delete': return t('Delete')
            case 'view': return t('View')
            default: return action
        }
    }

    const notImplemented = (action: string) => {
        alert(`Action ${action} not implemented`)
        return null
    }

    const table = useTable()
    const result = table.handleCellAction ? table.handleCellAction(action) : null
    const onActionClick = result?.onClick || onClick || notImplemented
    const className = result?.className || action
    if (result?.label) label = result.label
    if (!label) label = predefinedLabel()

    return <span onClick={() => onActionClick(action, id, data)} className={className} {...rest}>{label}</span>
}

Table.Column = RsTable.Column as any
Table.HeaderCell = HeaderCell
Table.Row = Row
Table.Cell = Cell
Table.CellAction = CellAction

export default Table